import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { isNotEmpty } from '../../lib/ep.js';
import { Stage, Layer, Rect, Text, Circle, Line, Label, Tag, Group, Path } from 'react-konva';
import Konva from 'konva';

const HiddenCanvas = forwardRef(({ stage, ...props }, ref) => {
	const a = [];
	if(stage.hidden_areas != undefined) {
		for(var i in stage.hidden_areas) {
			a.push({ ...stage.hidden_areas[i] });
		}
	}

	const score = useRef(0);
	const areas = (stage?.hidden_areas ?? []).map((area) => ({ ...area }));

	const [ found_areas, setFoundAreas ] = useState({});
	const [ full_loaded, setFullLoaded ] = useState(false);

	const creating_color = '215, 35, 60'; // red
	const ok_color = stage?.color_ok ?? 'rgba(120, 195, 0, 1)'; // green
	const ko_color = stage?.color_ko ?? 'rgba(215, 35, 60, 1)'; // red
	const display_outter_border = stage?.display_outter_border ?? true;

	const intersection_threshold = 25; // number of segments to divide evaluating line to check for intersections
	const mount_slices = 6;
	const delay_to_success = 0.5; // in seconds

	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;
	const is_edit_mode = props?.is_edit_mode ?? true;

	const edit_mode = (is_builder && !is_test && is_edit_mode) ? true : false;

	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

	const default_polygon = {
		points:[],
	};

	const refs = useRef([]);
	const size = useRef(600);
	const new_polygon = useRef(default_polygon);
	const is_zooming = useRef(false);
	const is_dragging = useRef(false);
	const tap_pos = useRef({ x:0, y:0 });
	const lastDist = useRef(0);
	const points_xy = useRef([]);
	const is_playable = useRef(false);

	/*START SOUNDS*/
	const foundPlay = () => props?.sound_fx?.found.play();
	const errorPlay = () => props?.sound_fx?.error.play();
	const successPlay = () => props?.sound_fx?.success.play();
	const failPlay = () => props?.sound_fx?.fail.play();
	/*END SOUNDS*/

	const preventLeaving = (e) => {
		e.returnValue = "There are unsaved changes. Leave now?";
	};

	useEffect(() => {
		if(is_builder) return;

		window.addEventListener('beforeunload', preventLeaving);

		return () => {
			if(props.endSoundtrack != undefined) props.endSoundtrack(true);

			window.removeEventListener('beforeunload', preventLeaving);
		};
	}, []);

	useEffect(() => {
		if (!props.indexed_images?.hidden_image) return;
		gameInit();
	}, [
		props.indexed_images,
	]);

	const gameInit = () => {
		if(props.adaptToContainer != undefined) {
			props.adaptToContainer((_size) => {
				size.current = _size;
				setFullLoaded(true);

				setTimeout(() => {
					if(refs.current['layer'] == undefined) return;

					if(is_playable.current === false) {
						mountBoardAnimation();
					}
				}, (delay_to_success * 1000));
			});
		}
	};

	useImperativeHandle(ref, () => ({
		stopGame(success_game, cb) {
			is_playable.current = false;

			(success_game) ? successPlay() : failPlay();

			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);

			unmountBoardAnimation(success_game, () => {
				setTimeout(() => {
					cb(success_game);
				}, 500);
			});
		},
	}), []);

	const clickImage = () => {
		const pos = refs.current['stage'].getPointerPosition();
		
		if(edit_mode) {
			checkPosition(pos);
			return;
		}
		
		if(is_playable.current === false) return;
		clickImageTapEmulated(pos);
	};

	const clickImageTapEmulated = (pos) => {
		is_zooming.current = false;
		is_dragging.current = false;

		const has_found = refs.current['stage'].getIntersection({ x:pos.x, y:pos.y }, 'Line');

		if(has_found) return;
		
		props.doHit();

		const x = (pos.x - refs.current['stage'].x()) / refs.current['stage'].scaleX();
		const y = (pos.y - refs.current['stage'].y()) / refs.current['stage'].scaleY();

		addMissedClick(x, y);

		errorPlay();
	};

	const addMissedClick = (x, y) => {
		const fail_props = {
			stroke:ko_color,
			strokeWidth:5,
			lineCap:'round',
			x,
			y,
		};

		const l1 = new Konva.Line({
			...fail_props,
			points:[-size_point, size_point, size_point, -size_point],
		});

		const l2 = new Konva.Line({
			...fail_props,
			points:[-size_point, -size_point, size_point, size_point],
		});

		refs.current['layer'].add(l1);
		refs.current['layer'].add(l2);

		redrawLayer();
	};

	const removeLast = () => {
		const num_points = new_polygon.current.points.length;
		if(num_points < 2) {
			// only 1 point in the area, so we delete it
			clearCurrentArea();
			return;
		}

		new_polygon.current.points.pop();
		new_polygon.current.points.pop();
		points_xy.current.pop();
		updateNewLine();
	};

	const redrawLayer = () => {
		refs.current['layer'].draw();
	};

	const saveArea = () => {
		const position_percentage_x = new_polygon.current.x / size.current;
		const position_percentage_y = new_polygon.current.y / size.current;

		const new_area = {
			points:[],
			x:position_percentage_x,
			y:position_percentage_y,
		};

		new_polygon.current.points.map((point) => {
			new_area.points.push(point / size.current);
		});

		const last_point_x = new_polygon.current.points[new_polygon.current.points.length - 2];
		const last_point_y = new_polygon.current.points[new_polygon.current.points.length - 1];

		let has_insersected = false;
		for(let i = 0; i < intersection_threshold; i++) {
			const point_x = (last_point_x / intersection_threshold) * i + new_polygon.current.x;
			const point_y = (last_point_y / intersection_threshold) * i + new_polygon.current.y;

			const element_intersected_close_line = refs.current['layer'].getIntersection({ x:point_x, y:point_y }, 'Line');

			if(element_intersected_close_line) {
				if(element_intersected_close_line._id != refs.current['new_polygon']._id) {
					alert("Polygon can't be closed since it intersects with other polygon(s)");
					has_insersected = true;
					break;
				}
			}
		}

		let has_collision = false;

		if(!has_insersected && !has_collision) {
			const _areas = [ ...areas ];
			_areas.push(new_area);

			if(props?.saveAreas != undefined) props.saveAreas(_areas);

			clearCurrentArea();
			
			removePoint();
		}
	};

	const clearCurrentArea = () => {
		refs.current['actions'].x(1000);
		points_xy.current = [];
		new_polygon.current = default_polygon;
		if(refs.current['new_polygon_g'] != undefined) refs.current['new_polygon_g'].destroy();
		redrawLayer();
	};

	/**
	 * converts KonvaJS points array type (i.e. [x1, y1, x2, y2, x3, y3])
	 * into a points {x,y} arrays (i.e. [{x1, y1}, {x2, y2}, {x3, y3}])
	 */
	const convertPointsToPairOfCoordinates = (points) => {
		if(points[0].x != undefined && points[0].y != undefined) return points;

		const _points_xy = [];
		for(let i = 0; i < points.length; i += 2) {
			_points_xy.push({
				x:parseFloat(points[i]),
				y:parseFloat(points[i + 1]),
			});
		}

		return _points_xy;
	};

	const getCenterPolygon = (points) => {
		const pts = convertPointsToPairOfCoordinates(points);

		const nPts = pts.length;
		let x = 0;
		let y = 0;
		let f;
		let j = nPts - 1;
		let p1;
		let p2;

		for(let i = 0; i < nPts; j = i++) {
			p1 = pts[i];
			p2 = pts[j];

			f = p1.x * p2.y - p1.y * p2.x;
			x += (p1.x + p2.x) * f;
			y += (p1.y + p2.y) * f;
		}

		f = getArea(pts) * 6;

		return {
			x:x / f,
			y:y / f,
		};
	};

	const getArea = (points) => {
		const pts = points;
		const nPts = pts.length;
		let j = nPts - 1;
		let p1;
		let p2;
		let area = 0;

		for(let i = 0; i < nPts; j = i++) {
			p1 = pts[i];
			p2 = pts[j];

			area += p1.x * p2.y;
			area -= p1.y * p2.x;
		}

		area /= 2;

		return area;
	};

	const checkPosition = (pos) => {
		if(new_polygon.current.x == undefined) new_polygon.current.x = pos.x;
		if(new_polygon.current.y == undefined) new_polygon.current.y = pos.y;

		if(new_polygon.current.points == undefined || new_polygon.current.points.length == 0) {
			new_polygon.current.points = [];
			new_polygon.current.points.push(0);
			new_polygon.current.points.push(0);
		} else {
			const num_points = new_polygon.current.points.length;

			const new_point_x = pos.x - new_polygon.current.x;
			const new_point_y = pos.y - new_polygon.current.y;

			const has_intersections = checkInsersections({
				x1:new_polygon.current.points[num_points - 2],	// last point x
				y1:new_polygon.current.points[num_points - 1],	// last point y
				x2:new_point_x,
				y2:new_point_y,
			});

			if(!has_intersections) {
				new_polygon.current.points.push(new_point_x);
				new_polygon.current.points.push(new_point_y);
			}
		}

		points_xy.current = [];
		if(new_polygon.current.points != undefined) {
			for(let i = 0; i < new_polygon.current.points.length; i += 2) {
				points_xy.current.push({
					x:new_polygon.current.points[i],
					y:new_polygon.current.points[i + 1],
				});
			}
		}

		updateNewLine();
	};

	const updateNewLine = () => {
		if(!edit_mode) return;

		if(refs.current['new_polygon_g'] != undefined) refs.current['new_polygon_g'].destroy();

		const g = new Konva.Group({
			x:new_polygon.current.x,
			y:new_polygon.current.y,
		});

		refs.current['new_polygon_g'] = g;

		const new_line = new Konva.Line({
			points:new_polygon.current.points,
			hitStrokeWidth:0,
			strokeWidth:2,
			stroke:`rgba(${creating_color}, 1)`,
			fill:`rgba(${creating_color}, 0.2)`,
			closed:true,
			perfectDrawEnabled:false,
		});

		refs.current['new_polygon'] = new_line;
		refs.current['new_polygon_g'].add(new_line);

		if(points_xy.current.length > 0) {
			points_xy.current.map((point, index) => {
				const p = new Konva.Rect({
					x:point.x,
					y:point.y,
					offsetX:size_point / 2,
					offsetY:size_point / 2,
					width:size_point,
					height:size_point,
					fill:`rgba(${creating_color}, 1)`,
					draggable:true,
				});

				p.on('dragmove', () => {
					changeLine(index);
				});

				refs.current[`point_${index}`] = p;

				refs.current['new_polygon_g'].add(p);
			});
		}

		refs.current['layer'].add(g);

		moveActions();

		redrawLayer();
	};

	const moveActions = () => {
		const bb = refs.current['actions'].getClientRect();
		const actions_w = bb.width;
		const actions_h = bb.height;

		const x = new_polygon.current.x + points_xy.current[points_xy.current.length - 1].x + 10;
		const y = new_polygon.current.y + points_xy.current[points_xy.current.length - 1].y + 10;

		refs.current['actions'].setAttrs({
			x:Math.max(20, Math.min(x, (size.current - actions_w - 10))),
			y:Math.max(20, Math.min(y, (size.current - actions_h - 10))),
		});
	}

	const checkInsersections = (line) => {
		let has_insersected = false;

		const _points_xy = [];
		if(new_polygon.current.points != undefined) {
			for(var i = 0; i < new_polygon.current.points.length; i += 2) {
				_points_xy.push({
					x:new_polygon.current.points[i],
					y:new_polygon.current.points[i+1],
				});
			}
		}

		if(_points_xy.length > 2) {
			for(let index in _points_xy) {
				if(index > 0) {
					let new_line = {
						x1:_points_xy[index - 1].x,
						y1:_points_xy[index - 1].y,
						x2:_points_xy[index].x,
						y2:_points_xy[index].y,
					};

					const min_value = Math.abs(Math.min(line.x1, line.y1, line.x2, line.y2, new_line.x1, new_line.y1, new_line.x2, new_line.y2));

					has_insersected = line_intersect(line.x1 + min_value , line.y1 + min_value, line.x2 + min_value, line.y2 + min_value, new_line.x1 + min_value, new_line.y1 + min_value, new_line.x2 + min_value, new_line.y2 + min_value);
					if(has_insersected) break;
				}
			}
		}

		if(!has_insersected) {
			const this_polygon = refs.current['new_polygon'];

			for(var i = 0; i < intersection_threshold; i++) {
				const xx = line.x1 + ((line.x2 - line.x1) / intersection_threshold) * i + new_polygon.current.x;
				const yy = line.y1 + ((line.y2 - line.y1) / intersection_threshold) * i + new_polygon.current.y;

				const element_intersected = refs.current['layer'].getIntersection({ x:xx, y:yy }, 'Line');
				if(element_intersected) {
					if(element_intersected._id != this_polygon._id) {
						has_insersected = true;
						break;
					}
				}
			}
		}

		return has_insersected;
	};

	const line_intersect = (x1, y1, x2, y2, x3, y3, x4, y4) => {
		// Check if none of the lines are of length 0
		if((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) return false;
		if((x1 === x4 && y1 === y4)) return false;

		let denominator = ((y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1));

		// Lines are parallel
		if(denominator === 0) return false;

		let ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
		let ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

		// is the intersection along the segments
		if(ua < 0 || ua > 1 || ub < 0 || ub > 1) return false;

		// Return a object with the x and y coordinates of the intersection
		const x = x1 + ua * (x2 - x1);
		const y = y1 + ua * (y2 - y1);

		return { x, y };
	};

	const area_found = (area_id) => {
		if(edit_mode) return;

		if(found_areas[area_id] != undefined) return;
		
		setFoundAreas((areas) =>({ ...areas, [area_id]:1 }));
		
		foundPlay();
		
		props.doHit(Object.keys(found_areas).length+1 == areas.length);

		if(props.onFound != undefined) props.onFound(area_id);

		score.current += 100;
		props.onScore(score.current);
		if(Object.keys(found_areas).length+1 == areas.length) {
			props.onEnded(true);
		}
	};

	const changeLine = (index) => {
		if(refs.current[`point_${index}`] == undefined) return;

		const point = refs.current[`point_${index}`].attrs;
		points_xy.current[index].x = point.x;
		points_xy.current[index].y = point.y;

		new_polygon.current.points = [];
		points_xy.current.map((point) => {
			new_polygon.current.points.push(point.x);
			new_polygon.current.points.push(point.y);
		});

		updateAreaPoints();
	};

	const updateAreaPoints = () => {
		if(refs.current['new_polygon'] == undefined) return;

		refs.current['new_polygon'].setAttrs({
			points:new_polygon.current.points,
		});

		moveActions();
		redrawLayer();
	};

	const getDistance = (p1, p2) => {
		return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
	};

	const touchEventsStart = (e) => {
		if(edit_mode) return;
		if(is_playable.current === false) return;

		const touch1 = e.evt.touches[0];

		if(e.evt.touches.length == 2) {
			const touch2 = e.evt.touches[1];

			is_zooming.current = true;

			const dist = getDistance({
				x:touch1.clientX,
				y:touch1.clientY,
			}, {
				x:touch2.clientX,
				y:touch2.clientY,
			});

			if(!lastDist.current) lastDist.current = dist;

			const scale = scaleLimits((refs.current['stage'].scaleX() * dist) / lastDist.current);
			stageScaleLimits(scale);

			const angle = Math.atan((touch2.clientY - touch1.clientY) / (touch2.clientX - touch1.clientX));
			const mid_zoom_point = {
				x:touch1.clientX + dist / 2 * Math.cos(angle),
				y:touch1.clientY + dist / 2 * Math.sin(angle),
			}

			const pointer_pos = refs.current['stage'].getPointerPosition();

			const newPosition = {
				x:(mid_zoom_point.x / scale - pointer_pos.x) * scale,
				y:(mid_zoom_point.y / scale - pointer_pos.y) * scale,
			};

			refs.current['stage'].position(newPosition);
			stageBoundLimits();

			refs.current['stage'].batchDraw();
			lastDist.current = dist;
		} else if(e.evt.touches.length == 1) {
			is_dragging.current = true;

			const pointer_pos = refs.current['stage'].getPointerPosition();

			tap_pos.current = {
				x:touch1.clientX,
				y:touch1.clientY,
				pointer_x:pointer_pos.x,
				pointer_y:pointer_pos.y,
			};
		}
	};

	const dragMove = (e) => {
		if(edit_mode) return;

		if(is_zooming.current) {
			touchEventsStart(e);
		} else {
			is_dragging.current = true;
		}

		stageBoundLimits();
	};

	const stageBoundLimits = () => {
		const stage_x = refs.current['stage'].x();
		const stage_y = refs.current['stage'].y();

		const min_x = size.current * refs.current['stage'].scaleX() - size.current;
		const min_y = size.current * refs.current['stage'].scaleY() - size.current;

		if(stage_x > 0) refs.current['stage'].x(0);
		if(stage_x < -min_x) refs.current['stage'].x(-min_x);
		if(stage_y > 0) refs.current['stage'].y(0);
		if(stage_y < -min_y) refs.current['stage'].y(-min_y);
	};

	const scaleLimits = (scale) => {
		return Math.min(2.5, Math.max(1, scale));
	};

	const stageScaleLimits = (scale) => {
		if(refs.current['stage'] == undefined) return;

		refs.current['stage'].setAttrs({
			scaleX:scale,
			scaleY:scale,
		});
	};

	const touchEventsStop = (e) => {
		if(edit_mode) return;

		if(e.evt.changedTouches.length == 1) {
			// dragging or tapping
			const moved_dist = getDistance(tap_pos.current, {
				x:e.evt.changedTouches[0].clientX,
				y:e.evt.changedTouches[0].clientY,
			});

			if(moved_dist < 0.01) {
				clickImageTapEmulated({
					x:tap_pos.current.pointer_x,
					y:tap_pos.current.pointer_y,
				});
			}
		}

		lastDist.current = 0;

		setTimeout(() => {
			is_zooming.current = false;
			is_dragging.current = false;
		}, 300);
	};

	const displayLabel = (index) => {
		if(!edit_mode) return;
		refs.current[`label_${index}`].opacity(1);
		redrawLayer();
	};

	const hideLabel = (index) => {
		if(!edit_mode) return;
		refs.current[`label_${index}`].opacity(0);
		redrawLayer();
	};

	const mouseWheelZoom = (e) => {
		if(edit_mode) return;
		if(is_playable.current === false) return;

		const stage = refs.current['stage'];

		e.evt.preventDefault();
		var oldScale = stage.scaleX();

		const pointer = stage.getPointerPosition();

		const startPos = {
			x:(pointer.x - stage.x()) / oldScale,
			y:(pointer.y - stage.y()) / oldScale,
		};

		const deltaYBounded = !(e.evt.deltaY % 1) ? Math.abs(Math.min(-10, Math.max(10, e.evt.deltaY))) : Math.abs(e.evt.deltaY);

		const scaleBy = 1.01 + deltaYBounded / 70;
		const newScale = scaleLimits((e.evt.deltaY > 0) ? (oldScale / scaleBy) : (oldScale * scaleBy));
		stageScaleLimits(newScale);

		const newPosition = {
			x:(pointer.x / newScale - startPos.x) * newScale,
			y:(pointer.y / newScale - startPos.y) * newScale,
		};

		stage.position(newPosition);
		stageBoundLimits();

		stage.batchDraw();
	};

	const mountBoardAnimation = () => {
		let finished = 0;
		[...Array(mount_slices).keys()].map((foo, p) => {
			if(refs.current[`piece_${p}`] == undefined) return;

			refs.current[`piece_${p}`].setAttrs({
				x:((p % 2 == 0) ? 1 : -1) * size.current,
			});

			setTimeout(() => {
				refs.current[`piece_${p}`].to({
					x:0,
					duration:1,
					easing:Konva.Easings.StrongEaseInOut,
					onFinish:() => {
						finished++;

						if(finished == mount_slices && refs.current['bg_image_mount'] != undefined) {
							refs.current['bg_image_mount'].setAttrs({
								opacity:0,
							});

							refs.current['bg_image'].setAttrs({
								opacity:1,
							});

							refs.current['bg_image'].cache(default_cache_props);

							is_playable.current = true;

							if(refs.current['whole_tapper'] != undefined) {
								refs.current['whole_tapper'].setAttrs({
									y:size.current + 10,
								});
							}
							props.startTimer();
						}
					},
				});
			}, (p * 100));
		});
	};

	const unmountBoardAnimation = (is_solved = true, cb) => {
		refs.current['whole_tapper'].setAttrs({
			y:0,
		});

		setTimeout(() => {
			if(refs.current['bg_image'] == undefined) return;
			if(refs.current['stage'] == undefined) return;

			refs.current['bg_image'].to({
				saturation:-50,
				duration:1.4,
				easing:Konva.Easings.StrongEaseInOut,
			});

			refs.current['stage'].to({
				x:0,
				y:0,
				scaleX:1,
				scaleY:1,
				duration:1.4,
				easing:Konva.Easings.StrongEaseInOut,
				onFinish:() => {
					if(props.onResetFounds != undefined) props.onResetFounds();
					cb();
				},
			});
		}, 400);
	};

	const bg_image = props.indexed_images.hidden_image;
	
	if(full_loaded === false) return null;

	const size_point = 10;
	const bg_image_scale = size.current / Math.min((bg_image?.image_w ?? 600), (bg_image?.image_h ?? 600));
	const main_classes = ['canvas_cont'];
	if(display_outter_border === false) main_classes.push('not_bordered');

	return (
		<Stage
			ref={(node) => { refs.current['stage'] = node; }}
			className={main_classes.join(" ")}
			width={size.current}
			height={size.current}
			draggable={((is_zooming.current || edit_mode) ? false : true)}
			onDragMove={(e) => dragMove(e)}
			onTouchMove={(e) => touchEventsStart(e)}
			onTouchEnd={(e) => touchEventsStop(e)}
			onWheel={(e) => mouseWheelZoom(e)}
		>
			<Layer
				ref={(node) => { refs.current['layer'] = node; }}
			>
				{(!is_builder || is_test) && (
					<Group
						ref={(node) => { refs.current['bg_image_mount'] = node; }}
					>
						{[...Array(mount_slices).keys()].map((foo, p) => {
							return (
								<Rect
									key={`piece_${p}`}
									ref={(node) => { refs.current[`piece_${p}`] = node; }}
									x={((p % 2 == 0) ? 1 : -1) * size.current}
									y={p * size.current / mount_slices}
									width={size.current}
									height={size.current / mount_slices}
									fillPatternImage={bg_image?.fillPatternImage}
									fillPatternRepeat="no-repeat"
									fillPatternScaleX={bg_image_scale}
									fillPatternScaleY={bg_image_scale}
									fillPatternOffsetY={p * size.current / mount_slices / bg_image_scale}
								/>
							)
						})}
					</Group>
				)}

				<Group
					ref={(node) => { refs.current['whole_tapper'] = node; }}
					y={0}
				>
					<Rect
						width={size.current}
						height={size.current}
						fill='white'
						opacity={0}
					/>
				</Group>

				<Rect
					ref={(node) => { refs.current['bg_image'] = node; }}
					x={0}
					y={0}
					opacity={(!is_builder || is_test) ? 0 : 1}
					width={size.current}
					height={size.current}
					fillPatternImage={bg_image?.fillPatternImage}
					fillPatternRepeat="no-repeat"
					fillPatternScaleX={bg_image_scale}
					fillPatternScaleY={bg_image_scale}
					filters={[Konva.Filters.HSL]}
					onClick={(e) => clickImage(e)}
					onTap={(e) => clickImage(e)}
					onMouseEnter={(edit_mode) ? () => { document.body.style.cursor = 'pointer'; } : null}
					onMouseLeave={(edit_mode) ? () => { document.body.style.cursor = 'default'; } : null}
				/>

				{areas.map((area, index) => {
					//To paint Areas
					const x = area.x * size.current;
					const y = area.y * size.current;

					const points = [];
					for(let i = 0; i < area.points.length; i++) {
						points.push(area.points[i] * size.current);
					}

					//To paint Areas founded
					const middle_point = getCenterPolygon(area.points);
					const x_middle = (middle_point.x + parseFloat(area.x)) * size.current;
					const y_middle = (middle_point.y + parseFloat(area.y)) * size.current;

					const area_name = (isNotEmpty(area, 'name')) ? area.name : `Area_${index}`;

					return (
						<Group
							key={`area${index}`}
							onMouseEnter={() => displayLabel(index)}
							onMouseLeave={() => hideLabel(index)}
						>
							<Line
								ref={(node) => { refs.current[`area${index}`] = node; }}
								x={x}
								y={y}
								points={points}
								strokeWidth={2}
								stroke={(edit_mode) ? `rgba(${creating_color}, 1)` : 'transparent'}
								fill={(edit_mode) ? `rgba(${creating_color}, 0.5)` : 'transparent'}
								closed={true}
								draggable={false}
								perfectDrawEnabled={false}
								onClick={(e) => area_found(area.id)}
								onTap={(e) => area_found(area.id)}
							/>

							{(edit_mode) && (
								<Label
									ref={(node) => { refs.current[`label_${index}`] = node; }}
									x={x_middle}
									y={y_middle}
									opacity={0}
								>
									<Tag
										fill='black'
									/>

									<Text
										text={area_name}
										fill='white'
										fontSize={15}
										padding={5}
										fontFamily="Arial"
									/>
								</Label>
							)}

							{(!edit_mode && found_areas[area.id] != undefined) && (
								<Circle
									x={x_middle}
									y={y_middle}
									radius={15}
									fill='transparent'
									stroke={ok_color}
									strokeWidth={5}
								/>
							)}
						</Group>
					)
				})}

				{(edit_mode) && (
					<Group
						ref={(node) => { refs.current['actions'] = node; }}
						x={1000}
						offsetX={size_point / 2}
						offsetY={size_point / 2}
					>
						<Group
							onClick={clearCurrentArea}
						>
							<Rect
								width={40}
								height={40}
								fill='white'
							/>

							<Path
								data="M9.2,9.2L30.8,30.8"
								stroke={ko_color}
								strokeWidth={2}
								lineCap='round'
							/>

							<Path
								data="M9.2,30.8L30.8,9.2"
								stroke={ko_color}
								strokeWidth={2}
								lineCap='round'
							/>
						</Group>

						<Group
							x={45}
							onClick={removeLast}
						>
							<Rect
								width={40}
								height={40}
								fill='white'
							/>

							<Path
								data="M22.92,27.43L28.48,28.48L27.57,34.08"
								stroke={ok_color}
								strokeWidth={2}
								lineCap='round'
							/>

							<Path
								data="M28.49,28.49C26.31,30.66,23.31,32,20,32c-6.63,0-12-5.37-12-12S13.37,8,20,8s12,5.37,12,12"
								stroke={ok_color}
								strokeWidth={2}
								lineCap='round'
							/>
						</Group>

						<Group
							x={90}
							onClick={saveArea}
						>
							<Rect
								width={40}
								height={40}
								fill='white'
							/>

							<Path
								data="M5.6,23.6L12.8,30.8L34.4,9.2"
								stroke={ok_color}
								strokeWidth={2}
								lineCap='round'
							/>
						</Group>
					</Group>
				)}
			</Layer>
		</Stage>
	);
});

export default HiddenCanvas;